import { Menu, Modal } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useRef, useState } from 'react';
import { IconAlertTriangle, IconBug, IconCamera, IconCameraPlus, IconChevronDown, IconCircleCheck, IconDots, IconExternalLink, IconFileTypePdf, IconQuestionMark, IconUpload } from '@tabler/icons-react';
import { IconButton, PrimaryButton } from 'components/Buttons/PrimayButton/PrimaryButton';
import { Dropdown } from 'components/Input/Dropdown/Dropdown';
import { TextArea, TextBox } from 'components/Input/TextBox/TextBox';
import BugReportModal from 'components/Modal/BugReportModal';
import CameraCapture from 'components/Capture/CameraCapture';
import InstructionModal from 'components/Modal/InstructionModal';
import PhotoModal from 'components/Modal/PhotoModal';
import Photo, { PhotoProps } from 'components/Photo/Photo';
import TopBar from 'components/TopBar/TopBar';
import { handleGeneratePdf } from 'helpers/pdfGen';
import { useNavigate } from 'react-router-dom';
import theme from 'styles/theme.module.scss';
import styles from './MainPage.module.scss';
import usePhotoProcessor from './usePhotoProcessor';

const MainPage = () => {
    const navigate = useNavigate();

    const { state, handlers } = usePhotoProcessor()
    // The guided in-app camera ("New Photo") is a mobile-only affordance; on PC
    // people add photos via the upload tile instead.
    const isDesktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });
    const addGalleryPhoto: PhotoProps = { src: "/test_img.jpg", label: "Upload from Camera Roll", state: "upload" }

    // Drag-and-drop image files anywhere over the photos section.
    const [isDragging, setDragging] = useState(false);
    const dragDepth = useRef(0);

    const isFileDrag = (e: React.DragEvent) =>
        Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const handleDragEnter = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
    };
    const handleDragOver = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };
    const handleDragLeave = () => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
            dragDepth.current = 0;
            setDragging(false);
        }
    };
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        Array.from(e.dataTransfer.files ?? []).forEach((file) => {
            if (file.type.startsWith('image/')) handlers.addPhotoFromFile(file);
        });
    };

    const [instructionOpened, { open: instructionOpen, close: instructionClose }] = useDisclosure(false);
    const [bugReportOpened, { open: bugReportOpen, close: bugReportClose }] = useDisclosure(false);
    const [captureOpened, { open: captureOpen, close: captureClose }] = useDisclosure(false);
    const [newCaptureOpened, { open: newCaptureOpen, close: newCaptureClose }] = useDisclosure(false);
    // Mobile only: inspection details collapse behind a live summary so the
    // camera is the first thing on screen. Desktop keeps the always-open pane.
    const [detailsOpen, { toggle: detailsToggle }] = useDisclosure(false);

    const formValues = state.form.getValues();
    const summaryParts = [formValues.assetCode, formValues.user, formValues.location]
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean);
    const summaryLabel = summaryParts.length
        ? summaryParts.join(' · ')
        : 'Tap to add asset code, inspector & site';

    // Confirm gating. Inspector name + at least one processed photo are required;
    // surface *why* the button is disabled rather than leaving it inert.
    const nameMissing = !(typeof formValues.user === 'string' && formValues.user.trim());
    const activePhotoCount = state.photos.filter((photo) => photo.state === 'active').length;
    const photosProcessing = state.photos.some((photo) => photo.state === 'loading');
    const confirmDisabled = nameMissing || activePhotoCount < 1 || photosProcessing;

    let confirmHint: string | null = null;
    if (photosProcessing) {
        confirmHint = 'Processing photos…';
    } else {
        const missing: string[] = [];
        if (nameMissing) missing.push('your inspector name');
        if (activePhotoCount < 1) missing.push('at least one photo');
        if (missing.length) confirmHint = `Add ${missing.join(' and ')} to confirm.`;
    }

    const handlePdfReport = () => {
        handleGeneratePdf(state, true);
        alert("Generating PDF Report.\nCheck your downloads folder.");
    };

    // Shared between the desktop two-pane and mobile layouts.
    const detailsFields = (
        <div className={styles.userFormContainer}>
            <TextBox
                label='Mech Asset Code'
                placeholder='SN-031'
                formInstance={state.form}
                name='assetCode'
            />
            <Dropdown
                data={[{ value: '1.00', label: '1.00 mm' }, { value: '0.80', label: '0.80 mm' }]}
                label='Baseline Aperture Width'
                placeholder='Pick a value'
                formInstance={state.form}
                name='baselineWidth'
            />
            <TextBox
                label='Inspector Name'
                placeholder='Full Name'
                formInstance={state.form}
                name='user'
            />
            <TextBox
                label='Site Location'
                placeholder='Site Location'
                formInstance={state.form}
                name='location'
            />
            <TextArea
                label='Comments (optional)'
                placeholder='...'
                formInstance={state.form}
                name='comments'
                height="5rem"
            />
        </div>
    );

    const summaryBar = (
        <button
            type="button"
            className={`${styles.detailsSummary} ${nameMissing ? styles.detailsSummaryFlagged : ''}`}
            onClick={detailsToggle}
            aria-expanded={detailsOpen}
        >
            <span className={styles.summaryText}>
                <span className={styles.summaryLabel}>Inspection details</span>
                <span className={styles.summaryValue}>{summaryLabel}</span>
            </span>
            {nameMissing && <span className={styles.summaryFlag}>Name required</span>}
            <IconChevronDown
                className={`${styles.summaryChevron} ${detailsOpen ? styles.summaryChevronOpen : ''}`}
            />
        </button>
    );

    const photoGrid = (
        <div className={styles.photoGridContainer}>
            {state.photos.map((photo, index) => (
                <Photo key={index} src={photo.src} state={photo.state} label={photo.label} onClick={() => { handlers.openPhotoModal(photo) }} />
            ))}
            {/* Desktop keeps the upload tile; on mobile uploading is the dedicated
                Upload button in the pinned action pair. */}
            {isDesktop && (
                <Photo state={addGalleryPhoto.state} label={addGalleryPhoto.label} onClick={() => { state.galleryInputRef.current?.click() }} src={undefined} />
            )}

            {/* Hidden gallery input — quick "upload from camera roll" path (multi-select) */}
            <input
                type="file"
                accept="image/*"
                multiple
                ref={state.galleryInputRef} // Reference to the hidden input
                onChange={handlers.handlePhotoUpload}
                style={{ display: "none" }} // Completely hide the input
            />
        </div>
    );

    return (
        <div className={styles.app}>
            <TopBar
                left={
                    <IconButton variant="chrome" Icon={IconQuestionMark} onClick={instructionOpen} aria-label="Instructions" />
                }
                right={
                    <Menu shadow="md" width={220} position="bottom-end" radius={2} withinPortal>
                        <Menu.Target>
                            <IconButton variant="chrome" Icon={IconDots} aria-label="More actions" />
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item leftSection={<IconCameraPlus size={18} />} onClick={newCaptureOpen}>New Inspection</Menu.Item>
                            <Menu.Item leftSection={<IconFileTypePdf size={18} />} onClick={handlePdfReport}>PDF Report</Menu.Item>
                            <Menu.Divider />
                            <Menu.Item leftSection={<IconBug size={18} />} onClick={bugReportOpen}>Report a Bug</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                }
            />

            {isDesktop ? (
                /* Desktop: two-pane dashboard, both panes scroll independently. */
                <div className={styles.dashboard}>
                    <div className={styles.formPane}>
                        <div className={styles.formBody}>
                            <span className={theme.header2}>Aperture Screen AI</span>
                            <hr />
                            {detailsFields}
                        </div>
                    </div>

                    <div
                        className={styles.galleryPane}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {isDragging && (
                            <div className={styles.dropOverlay}>
                                <span>Drop photos to upload</span>
                            </div>
                        )}
                        <div className={styles.photoCollectionHeader}>
                            <span className={theme.header3}>Aperture Photos</span>
                        </div>
                        {photoGrid}
                    </div>
                </div>
            ) : (
                /* Mobile: everything (details summary included) scrolls; the
                   Capture/Upload pair is pinned just above the footer. */
                <>
                    <div
                        className={styles.photoScroll}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {isDragging && (
                            <div className={styles.dropOverlay}>
                                <span>Drop photos to upload</span>
                            </div>
                        )}
                        {summaryBar}
                        {detailsOpen && (
                            <div className={styles.formBodyMobile}>
                                {detailsFields}
                            </div>
                        )}
                        <div className={styles.photoCollectionHeader}>
                            <span className={theme.header3}>Aperture Photos</span>
                        </div>
                        {photoGrid}
                    </div>

                    <div className={styles.captureActions}>
                        <PrimaryButton
                            onClick={captureOpen}
                            label={"Capture Photo"}
                            Icon={IconCamera}
                        />
                        <PrimaryButton
                            variant="secondary"
                            onClick={() => { state.galleryInputRef.current?.click() }}
                            label={"Upload"}
                            Icon={IconUpload}
                            iconColor="black"
                        />
                    </div>
                </>
            )}

            <div className={styles.footer}>
                {confirmHint && (
                    <div className={styles.confirmHint}>
                        <IconAlertTriangle className={styles.confirmHintIcon} size={16} stroke={1.8} />
                        <span>{confirmHint}</span>
                    </div>
                )}
                <div className={styles.confirmWrap}>
                    <PrimaryButton
                        onClick={handlers.handleSubmit}
                        label={"Confirm Measurements"}
                        disabled={confirmDisabled}
                        Icon={undefined}
                    />
                </div>
            </div>

            <PhotoModal
                close={handlers.modalHandlers.photoClose}
                opened={state.photoOpened}
                // isOpen={state.isPhotoModalOpen}
                // onClose={() => { handlers.modalHandlers.onClose() }}
                onButtonClick={() => {
                    handlers.modalHandlers.onButtonClick()
                }}
                src={state.photoModalCurrentPhoto ? state.photoModalCurrentPhoto.annotatedSrc : null}
                label={state.photoModalCurrentPhoto ? state.photoModalCurrentPhoto.label : null}
            />

            <CameraCapture
                opened={captureOpened}
                onClose={captureClose}
                onCapture={(file, meta) => {
                    handlers.addPhotoFromFile(
                        file,
                        meta.orientation ? { orientation: meta.orientation } : undefined
                    );
                    captureClose();
                }}
            />

            <InstructionModal
                close={instructionClose}
                opened={instructionOpened}
                onButtonClick={() => {
                    instructionClose();
                }}
            />

            <BugReportModal
                close={bugReportClose}
                opened={bugReportOpened}
                form={state.bugReportForm}
                onButtonClick={() => {
                    handlers.handleSubmitBugReport();
                    bugReportClose();
                }}
            />

            <Modal
                onClose={newCaptureClose}
                opened={newCaptureOpened}
                title="Start a new inspection?"
                centered
            >
                <div className={styles.modal}>
                    <p>This clears the current photos and asset code. Your name, location and baseline width are kept.</p>
                    <div className={styles.modalActions}>
                        <PrimaryButton variant="secondary" onClick={newCaptureClose} label="Cancel" />
                        <PrimaryButton
                            onClick={() => {
                                handlers.startNewCapture();
                                newCaptureClose();
                            }}
                            label="Start New"
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                onClose={handlers.confirmationModalHandlers.confirmationClose}
                opened={state.confirmationOpened}
                title={null}
                centered
                radius={2}
            >
                <div className={styles.successModal}>
                    <div className={styles.successIcon}>
                        <IconCircleCheck size={44} stroke={1.8} />
                    </div>
                    <span className={theme.header3}>Inspection submitted</span>
                    <p className={styles.successText}>
                        Your measurements have been saved. Open the full inspection report to review or share it.
                    </p>
                    <div className={styles.successActions}>
                        <PrimaryButton
                            onClick={handlers.confirmationModalHandlers.onButtonClick}
                            label='View Report'
                            Icon={IconExternalLink}
                        />
                        <PrimaryButton
                            variant="secondary"
                            onClick={() => {
                                handlers.confirmationModalHandlers.confirmationClose();
                                newCaptureOpen();
                            }}
                            label='New Inspection'
                            Icon={IconCameraPlus}
                            iconColor="black"
                        />
                    </div>
                </div>
            </Modal>

        </div>
    )

}

export default MainPage;